import React from 'react';
import {
    List,
    Datagrid,
    DateField,
    TextField,
    FunctionField,
    Filter,
    TextInput,
    SelectInput,
    DateInput,
    TopToolbar,
    useRefresh,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Button,
    Chip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TruncatedId } from './TruncatedId';
import { TopUpViewToggle } from './TopUpViewToggle';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
    completed: 'success',
    pending: 'warning',
    processing: 'info',
    failed: 'error',
    cancelled: 'default',
    timeout: 'error',
    refunded: 'default',
};

const StatusChip = ({ status }: { status: string }) => {
    const translate = useTranslate();
    const color = STATUS_COLOR[status] || 'default';
    return (
        <Chip
            label={translate(`resources.app-topup-attempts.statuses.${status}`, { _: status })}
            color={color}
            size="small"
        />
    );
};

const AttemptsFilter = (props: any) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.app-topup-attempts.filters.search', {
                    _: 'Search (order # / gateway ref / user)',
                })}
                source="search"
                variant="outlined"
                size="small"
                alwaysOn
                resettable
                helperText={false}
                InputLabelProps={{ shrink: true }}
            />
            <SelectInput
                label={translate('resources.app-topup-attempts.filters.status', { _: 'Status' })}
                source="status"
                variant="outlined"
                size="small"
                alwaysOn
                helperText={false}
                emptyText={translate('resources.app-topup-attempts.filters.statusAll', { _: 'All' })}
                InputLabelProps={{ shrink: true }}
                sx={{ marginBottom: '0 !important' }}
                choices={[
                    { id: 'pending', name: translate('resources.app-topup-attempts.statuses.pending', { _: 'Pending' }) },
                    { id: 'processing', name: translate('resources.app-topup-attempts.statuses.processing', { _: 'Processing' }) },
                    { id: 'completed', name: translate('resources.app-topup-attempts.statuses.completed', { _: 'Completed' }) },
                    { id: 'failed', name: translate('resources.app-topup-attempts.statuses.failed', { _: 'Failed' }) },
                    { id: 'cancelled', name: translate('resources.app-topup-attempts.statuses.cancelled', { _: 'Cancelled' }) },
                    { id: 'timeout', name: translate('resources.app-topup-attempts.statuses.timeout', { _: 'Timeout' }) },
                    { id: 'refunded', name: translate('resources.app-topup-attempts.statuses.refunded', { _: 'Refunded' }) },
                ]}
            />
            <DateInput
                label={translate('resources.app-topup-attempts.filters.fromDate', { _: 'From' })}
                source="from_date"
                variant="outlined"
                size="small"
                alwaysOn
                helperText={false}
                InputLabelProps={{ shrink: true }}
            />
            <DateInput
                label={translate('resources.app-topup-attempts.filters.toDate', { _: 'To' })}
                source="to_date"
                variant="outlined"
                size="small"
                alwaysOn
                helperText={false}
                InputLabelProps={{ shrink: true }}
            />
        </Filter>
    );
};

const ListActions = () => {
    const refresh = useRefresh();
    const translate = useTranslate();
    return (
        <TopToolbar sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
                startIcon={<RefreshIcon />}
                onClick={refresh}
                variant="outlined"
                size="small"
            >
                {translate('resources.app-topup-attempts.actions.refresh', { _: 'Refresh' })}
            </Button>
        </TopToolbar>
    );
};

const MobileAttemptsList: React.FC = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        sx={{
                            py: 1.5,
                            px: 2,
                            borderBottom: '1px solid #F0F0F0',
                            cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/app-topup-attempts/${record.id}/show`)}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: 700 }}>
                                {record.currency || 'HKD'} {Number(record.amount || 0).toFixed(2)}
                            </Typography>
                            <StatusChip status={record.status} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                            {record.user_name && (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    <Person sx={{ fontSize: 14, color: '#999' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {record.user_name}
                                        {record.user_phone ? ` · ${record.user_phone}` : ''}
                                    </Typography>
                                </Box>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                {record.created_at ? new Date(record.created_at).toLocaleString() : ''}
                            </Typography>
                        </Box>
                        {record.gateway_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {record.gateway_name}
                                {record.mpay_response_code ? ` · code ${record.mpay_response_code}` : ''}
                            </Typography>
                        )}
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const AppTopUpAttemptsList: React.FC = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            <TopUpViewToggle active="attempts" />
            <List
                resource="app-topup-attempts"
                filters={<AttemptsFilter />}
                actions={<ListActions />}
                perPage={25}
                sort={{ field: 'created_at', order: 'DESC' }}
                title={translate('resources.app-topup-attempts.name', {
                    smart_count: 2,
                    _: 'Top-Up Attempts',
                })}
                empty={false}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                }}
            >
                {isMobile ? (
                    <MobileAttemptsList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick={(id) => `/app-topup-attempts/${id}/show`}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row:hover': {
                                backgroundColor: '#F0F7FF',
                            },
                        }}
                    >
                        <FunctionField
                            label={translate('resources.app-topup-attempts.fields.id', { _: 'ID' })}
                            render={(record: any) => <TruncatedId id={record.id} />}
                        />
                        <DateField
                            source="created_at"
                            label={translate('resources.app-topup-attempts.fields.createdAt', { _: 'Created' })}
                            showTime
                        />
                        <FunctionField
                            label={translate('resources.app-topup-attempts.fields.user', { _: 'User' })}
                            render={(record: any) => (
                                <Box>
                                    <Typography variant="body2">{record.user_name || '—'}</Typography>
                                    {record.user_email && (
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {record.user_email}
                                        </Typography>
                                    )}
                                    {record.user_phone && (
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            {record.user_phone}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        />
                        <FunctionField
                            label={translate('resources.app-topup-attempts.fields.amount', { _: 'Amount' })}
                            render={(record: any) => (
                                <Typography variant="body2" fontWeight="bold">
                                    {record.currency || 'HKD'} {Number(record.amount || 0).toFixed(2)}
                                </Typography>
                            )}
                        />
                        <TextField
                            source="gateway_name"
                            label={translate('resources.app-topup-attempts.fields.gateway', { _: 'Gateway' })}
                        />
                        <TextField
                            source="payment_method"
                            label={translate('resources.app-topup-attempts.fields.paymentMethod', { _: 'Method' })}
                        />
                        <TextField
                            source="mpay_response_code"
                            label={translate('resources.app-topup-attempts.fields.responseCode', { _: 'Code' })}
                        />
                        <FunctionField
                            label={translate('resources.app-topup-attempts.fields.status', { _: 'Status' })}
                            render={(record: any) => <StatusChip status={record.status} />}
                        />
                    </Datagrid>
                )}
            </List>
        </>
    );
};

export default AppTopUpAttemptsList;
